import { AbstractControl, AsyncValidatorFn, ValidationErrors } from '@angular/forms';
import { DataRepository } from '@database/workspace';
import { FormActionTypeEnum, PrimaryKeyValueModel } from '@shared';
import { combineLatest, first, map, Observable, of, switchMap } from 'rxjs';
import { ValidationKeys } from '../models/validation-keys';
import { QueryParamsProvider } from '@domain/routing';
import { ValidationsBuilderModel } from '../models';
import { FormHelper, NormalizeFormValuesForDatabaseService } from '../../services';

export class UniquePrimaryKeyBuilder {
	private validation: AsyncValidatorFn;
	private validationsMessages: string[] = [];

	public constructor(
		private entityName: string,
		private projectNumber: string,
		private columnName: string,
		private isChildTable: boolean,
		private primaryKeys: string[],
		private formActionType: FormActionTypeEnum,
		private queryParamsProvider: QueryParamsProvider,
		private dataRepository: DataRepository,
		private normalizeFormValuesForDatabaseService: NormalizeFormValuesForDatabaseService
	) {}

	public validate(): ValidationsBuilderModel | null {
		if (!this.isChildTable && this.primaryKeys.includes(this.columnName)) {
			const isComposedPrimaryKey: boolean = this.primaryKeys.length > 1;

			if (isComposedPrimaryKey) {
				this.validation = this.uniqueComposedPrimaryKeyValidation();

				this.validationsMessages.push(ValidationKeys.validationRules.uniqueComposedPrimaryKeyRule);
				this.validationsMessages.push(ValidationKeys.validationRules.uniqueDeletedComposedPrimaryKeyRule);
			} else {
				this.validation = this.uniquePrimaryKeyValidation();

				this.validationsMessages.push(ValidationKeys.validationRules.uniquePrimaryKeyRule);
				this.validationsMessages.push(ValidationKeys.validationRules.uniqueDeletedPrimaryKeyRule);
			}

			return { validation: this.validation, messages: this.validationsMessages };
		}

		return null;
	}

	private uniqueComposedPrimaryKeyValidation(): AsyncValidatorFn {
		return (control: AbstractControl): Observable<ValidationErrors | null> => {
			if (control?.value) {
				let excludedId = 0;

				// apply validation only if all columns (part of composed primary key) are not empty
				if (!this.allComposedPrimaryKeysAreNotEmpty(control)) {
					return of(null);
				}

				if (
					this.formActionType === FormActionTypeEnum.edit ||
					this.formActionType === FormActionTypeEnum.duplicate
				) {
					excludedId = Number(this.queryParamsProvider.entryId);
				}

				const primaryKeyAndValue: PrimaryKeyValueModel[] = this.primaryKeys.map((pk: string) => {
					const columnValue = FormHelper.getSiblingControl(control, pk)?.value;
					const primaryKeyValue = columnValue ? columnValue : null;
					return { primaryKey: pk, primaryKeyValue };
				}, []);

				return this.normalizeFormValuesForDatabaseService
					.preparePrimaryKeysValues$(this.entityName, primaryKeyAndValue)
					.pipe(
						switchMap((pkv: PrimaryKeyValueModel[]) =>
							combineLatest([
								this.dataRepository.isTablePrimaryKeyValueUnique$(
									this.entityName,
									this.projectNumber,
									pkv,
									excludedId,
									false
								),
								this.dataRepository.isTablePrimaryKeyValueUnique$(
									this.entityName,
									this.projectNumber,
									pkv,
									excludedId,
									true
								)
							]).pipe(
								map(([isUniquePrimaryKey, isUniqueDeletedPrimaryKey]) => {
									if (isUniquePrimaryKey && isUniqueDeletedPrimaryKey) {
										for (const pk of this.primaryKeys) {
											if (pk === this.columnName) {
												continue;
											}
											control.parent?.controls[pk].setErrors(null);
										}
									}

									if (!isUniquePrimaryKey) {
										return this.isUniqueComposedPrimaryKey(control);
									}

									if (!isUniqueDeletedPrimaryKey) {
										return this.isUniqueDeletedComposedPrimaryKey(control);
									}

									return null;
								}),
								first()
							)
						),
						first()
					);
			}
			return of(null);
		};
	}

	private uniquePrimaryKeyValidation(): AsyncValidatorFn {
		return (control: AbstractControl): Observable<ValidationErrors | null> => {
			if (control?.value) {
				let excludedId = 0;

				if (
					this.formActionType === FormActionTypeEnum.edit ||
					this.formActionType === FormActionTypeEnum.duplicate
				) {
					excludedId = Number(this.queryParamsProvider.entryId);
				}

				const primaryKeyAndValue: PrimaryKeyValueModel[] = this.primaryKeys.map((pk: string) => {
					const primaryKeyValue = pk === this.columnName ? control.value : null;
					return { primaryKey: pk, primaryKeyValue };
				}, []);
				return combineLatest([
					this.dataRepository.isTablePrimaryKeyValueUnique$(
						this.entityName,
						this.projectNumber,
						primaryKeyAndValue,
						excludedId,
						false
					),
					this.dataRepository.isTablePrimaryKeyValueUnique$(
						this.entityName,
						this.projectNumber,
						primaryKeyAndValue,
						excludedId,
						true
					)
				]).pipe(
					map(([isUniquePrimaryKey, isUniqueDeletedPrimaryKey]) => {
						if (!isUniquePrimaryKey) {
							return { [ValidationKeys.validationRules.uniquePrimaryKeyRule]: true };
						}

						if (!isUniqueDeletedPrimaryKey) {
							return { [ValidationKeys.validationRules.uniqueDeletedPrimaryKeyRule]: true };
						}

						return null;
					}),
					first()
				);
			}
			return of(null);
		};
	}

	private allComposedPrimaryKeysAreNotEmpty(control: AbstractControl): boolean {
		for (const pk of this.primaryKeys) {
			const columnValue = FormHelper.getSiblingControl(control, pk)?.value;
			if (typeof columnValue === 'string' && columnValue === '') {
				return false;
			}
		}

		return true;
	}

	private isUniqueComposedPrimaryKey(control: AbstractControl): ValidationErrors {
		for (const pk of this.primaryKeys) {
			if (pk === this.columnName) {
				continue;
			}
			control.parent?.controls[pk].setErrors({
				[ValidationKeys.validationRules.uniqueComposedPrimaryKeyRule]: true
			});
			control.parent?.controls[pk].markAsTouched({ onlySelf: true });
		}

		return { [ValidationKeys.validationRules.uniqueComposedPrimaryKeyRule]: true };
	}

	private isUniqueDeletedComposedPrimaryKey(control: AbstractControl): ValidationErrors {
		// for records marked as deleted (soft deleted)
		for (const pk of this.primaryKeys) {
			if (pk === this.columnName) {
				continue;
			}
			control.parent?.controls[pk].setErrors({
				[ValidationKeys.validationRules.uniqueDeletedComposedPrimaryKeyRule]: true
			});
			control.parent?.controls[pk].markAsTouched({ onlySelf: true });
		}

		return { [ValidationKeys.validationRules.uniqueDeletedComposedPrimaryKeyRule]: true };
	}
}
